import { useContext, useEffect, useRef } from 'react';
import { PageConfigContext } from './page-config-context';
import type { PageConfig } from './page-config-context';

export type { PageConfig };

interface PageConfigContextValue {
  config: PageConfig | null;
  setConfig: (config: PageConfig) => void;
}

export function usePageConfigContext(): PageConfigContextValue {
  const ctx = useContext(PageConfigContext);
  if (!ctx) {
    throw new Error('usePageConfigContext must be used within a PageConfigProvider');
  }
  return ctx;
}

function serializeConfig(config: PageConfig): string {
  return JSON.stringify({
    breadcrumbsId: config.breadcrumbsId,
    title: config.title,
    description: config.description,
    backLabel: config.backLabel,
    actionId: config.action?.id,
    actionText: config.action?.text,
    actionIcon: config.action?.icon,
    actionType: config.action?.type,
    hasSecondaryActions: !!config.secondaryActions,
    secondaryActionsKey: config.secondaryActionsKey ?? '',
  });
}

export function usePageConfig(config: PageConfig): void {
  const { setConfig } = usePageConfigContext();
  const configRef = useRef(config);
  configRef.current = config;

  const configKey = serializeConfig(config);

  useEffect(() => {
    setConfig(configRef.current);
  }, [configKey, setConfig]);
}
